import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { MDBTable, MDBTableBody, MDBTableHead } from "mdb-react-ui-kit";
import Header from "../components/Header";
import Loader from "../components/Loader";
import Paginate from "../components/Paginate";
import SideNav from "../components/SideNav";
import {
  fetchLoanStats,
  fetchLoanApplications,
  fetchLoanDetail,
  requestDecisionOtp,
  submitDecision,
  DECISION_OTP_RESET,
  DECISION_SUBMIT_RESET,
} from "../actions/loanActions";

const fmtDate = (d) => d.toISOString().slice(0, 10);

const STATUS_BY_ID = {
  1: "Draft", 2: "Submitted", 3: "PendingReview", 4: "UnderReview",
  5: "AwaitingInfo", 6: "Approved", 7: "Rejected", 8: "OfferAccepted",
  9: "Disbursing", 10: "Disbursed",
};

const loanModeLabel = (mode) => (mode === 1 || mode === "Micro" ? "Micro" : "Standard");
const loanModeIsStandard = (mode) => mode === 2 || mode === "Standard";

const STATUS_COLORS = {
  Draft: "#6c757d",
  Submitted: "#17a2b8",
  PendingReview: "#fd7e14",
  UnderReview: "#6f42c1",
  AwaitingInfo: "#ffc107",
  Approved: "#28a745",
  OfferAccepted: "#20c997",
  Disbursing: "#007bff",
  Disbursed: "#155724",
  Rejected: "#dc3545",
};

const GUARANTOR_STATUS_COLORS = {
  Pending: "#fd7e14",
  Opened: "#17a2b8",
  Completed: "#28a745",
  Declined: "#dc3545",
  Replaced: "#6c757d",
};

const STATUSES = [
  { label: "Draft", id: 1 },
  { label: "Submitted", id: 2 },
  { label: "PendingReview", id: 3 },
  { label: "UnderReview", id: 4 },
  { label: "AwaitingInfo", id: 5 },
  { label: "Approved", id: 6 },
  { label: "Rejected", id: 7 },
  { label: "OfferAccepted", id: 8 },
  { label: "Disbursing", id: 9 },
  { label: "Disbursed", id: 10 },
];

const formatMoney = (amount) =>
  amount != null
    ? `₦${Number(amount).toLocaleString("en-NG", { minimumFractionDigits: 2 })}`
    : "—";

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("en-NG", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const StatusBadge = ({ status }) => (
  <span
    style={{
      background: STATUS_COLORS[status] || "#6c757d",
      color: status === "AwaitingInfo" ? "#333" : "#fff",
      padding: "2px 10px",
      borderRadius: 12,
      fontSize: 11,
      fontWeight: 600,
      whiteSpace: "nowrap",
    }}
  >
    {status || "—"}
  </span>
);

const InfoRow = ({ label, value }) => (
  <div
    style={{
      display: "flex",
      justifyContent: "space-between",
      padding: "6px 0",
      borderBottom: "1px solid #f0f0f0",
      gap: 12,
    }}
  >
    <span style={{ color: "#888", fontSize: 13, minWidth: 150, flexShrink: 0 }}>{label}</span>
    <span style={{ color: "#222", fontSize: 13, fontWeight: 500, textAlign: "right", wordBreak: "break-word" }}>
      {value != null && value !== "" ? value : "—"}
    </span>
  </div>
);

const SectionCard = ({ title, children }) => (
  <div style={{ background: "#fff", borderRadius: 8, border: "1px solid #e8e8e8", padding: "14px 16px", marginBottom: 12 }}>
    <div style={{ fontWeight: 700, fontSize: 13, color: "#444", marginBottom: 10, paddingBottom: 6, borderBottom: "2px solid #f0f0f0", textTransform: "uppercase", letterSpacing: "0.5px" }}>
      {title}
    </div>
    {children}
  </div>
);

const Loans = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const initRef = useRef(true);

  const { userInfo } = useSelector((state) => state.admin2faLogin);

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [page, setPage] = useState(1);
  const [filterStatusId, setFilterStatusId] = useState("");
  const [filterLoanMode, setFilterLoanMode] = useState("");

  const [selectedLoan, setSelectedLoan] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const [actionPanel, setActionPanel] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [approvedAmount, setApprovedAmount] = useState("");
  const [approvedTenor, setApprovedTenor] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  const [infoNote, setInfoNote] = useState("");

  const { loading: statsLoading, data: statsData } = useSelector((s) => s.getLoanMetrics) || {};
  const { loading: listLoading, data: listData, error: listError } = useSelector((s) => s.getLoanApplications) || {};
  const { loading: detailLoading, data: detailData, error: detailError } = useSelector((s) => s.getLoanDetail) || {};
  const { loading: otpLoading, error: otpError } = useSelector((s) => s.reviewLoan) || {};
  const { loading: submitLoading, error: submitError } = useSelector((s) => s.approveLoan) || {};

  useEffect(() => {
    if (userInfo?.user?.role === "Account Officer") {
      navigate(`/officer/${userInfo?.user?.id}`, { replace: true });
    }
    if (initRef.current) {
      initRef.current = false;
      dispatch(fetchLoanStats());
      dispatch(fetchLoanApplications());
    }
  }, [dispatch, userInfo, navigate]);

  const refetchList = () =>
    dispatch(fetchLoanApplications(page, 50, fromDate, toDate, filterStatusId, filterLoanMode));

  const handleApplyDates = () => {
    setPage(1);
    dispatch(fetchLoanApplications(1, 50, fromDate, toDate, filterStatusId, filterLoanMode));
  };

  const handleFilter = () => {
    setPage(1);
    dispatch(fetchLoanApplications(1, 50, fromDate, toDate, filterStatusId, filterLoanMode));
  };

  const handleClearFilters = () => {
    setFilterStatusId("");
    setFilterLoanMode("");
    setPage(1);
    dispatch(fetchLoanApplications(1, 50, fromDate, toDate));
  };

  const handlePageChange = (p) => {
    setPage(p);
    dispatch(fetchLoanApplications(p, 50, fromDate, toDate, filterStatusId, filterLoanMode));
  };

  const handleView = (loan) => {
    setSelectedLoan(loan);
    resetActionPanel();
    dispatch(fetchLoanDetail(loan.id));
    setDrawerOpen(true);
    document.body.style.overflow = "hidden";
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setSelectedLoan(null);
    resetActionPanel();
    document.body.style.overflow = "";
  };

  const resetActionPanel = () => {
    setActionPanel("");
    setOtpSent(false);
    setOtpCode("");
    setApprovedAmount("");
    setApprovedTenor("");
    setRejectReason("");
    setInfoNote("");
    dispatch({ type: DECISION_OTP_RESET });
    dispatch({ type: DECISION_SUBMIT_RESET });
  };

  const handleSendOtp = async () => {
    try {
      const result = await dispatch(requestDecisionOtp(app.id));
      setOtpSent(true);
      toast.success(result?.message || "OTP sent to your email");
    } catch (e) {
      toast.error(e.message || "Failed to send OTP");
    }
  };

  const handleSubmitDecision = async () => {
    if (!otpCode.trim()) {
      toast.error("Please enter the OTP from your email");
      return;
    }
    let body = { otp: otpCode };
    if (actionPanel === "approve") {
      body.decision = "approved";
      if (app.loan_mode === 2) {
        if (!approvedAmount || !approvedTenor) {
          toast.error("Please enter the offered amount and tenor");
          return;
        }
        body.approved_amount = Number(approvedAmount);
        body.approved_tenor = Number(approvedTenor);
      }
    } else if (actionPanel === "reject") {
      if (!rejectReason.trim()) {
        toast.error("Please provide a rejection reason");
        return;
      }
      body.decision = "rejected";
      body.reject_reason = rejectReason;
    } else if (actionPanel === "request-info") {
      body.decision = "awaiting_info";
      if (infoNote.trim()) body.reject_reason = infoNote;
    }
    try {
      await dispatch(submitDecision(app.id, body));
      toast.success("Decision submitted successfully");
      dispatch(fetchLoanDetail(app.id));
      dispatch(fetchLoanStats());
      refetchList();
      resetActionPanel();
    } catch (e) {
      toast.error(e.message || "Failed to submit decision");
    }
  };

  const apps = listData?.data?.items || [];
  const listMeta = listData?.data || {};
  const totalPages = listMeta.total && listMeta.size ? Math.ceil(listMeta.total / listMeta.size) : 1;
  const stats = statsData?.data || {};

  // Detail shape: { application, contacts, guarantors, guarantors_complete, documents, schedule }
  const detail = detailData?.data || {};
  const app = detail.application || {};
  const contacts = detail.contacts || [];
  const guarantors = detail.guarantors || [];
  const guarantors_complete = detail.guarantors_complete ?? true;
  const documents = detail.documents || [];
  const schedule = detail.schedule || [];

  // Detail uses numeric status_id (4=UnderReview, 5=AwaitingInfo, 6=Approved)
  const canApprove = [4, 5].includes(app.status_id);
  const canReject = [4, 5, 6].includes(app.status_id);
  const canRequestInfo = app.status_id === 4;
  const hasActions = canApprove || canReject || canRequestInfo;
  const approveBlocked = app.loan_mode === 2 && !guarantors_complete;

  const metricCards = [
    { label: "Pending Review", value: stats.pending_review, color: "#fd7e14" },
    { label: "Under Review", value: stats.under_review, color: "#6f42c1" },
    { label: "Awaiting Info", value: stats.awaiting_info, color: "#e0a800" },
    { label: "Approved", value: stats.approved, color: "#28a745" },
    { label: "Disbursed", value: stats.disbursed, color: "#007bff" },
    { label: "Rejected", value: stats.rejected, color: "#dc3545" },
    { label: "Drafts", value: stats.drafts, color: "#6c757d" },
  ];

  return (
    <>
      <SideNav />
      <section className="page-container">
        <div className="page-content">
          <Header />
          <div className="page-body">
            <h1>Loans</h1>

            {/* Metric cards */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: 12, marginBottom: 24 }}>
              {metricCards.map((c) => (
                <div key={c.label}
                  style={{ background: "#fff", borderRadius: 8, padding: "16px", boxShadow: "0 1px 4px rgba(0,0,0,0.07)", borderLeft: `4px solid ${c.color}` }}>
                  <div style={{ fontSize: 24, fontWeight: 700, color: c.color }}>{statsLoading ? "—" : c.value ?? 0}</div>
                  <div style={{ fontSize: 12, color: "#666", marginTop: 4 }}>{c.label}</div>
                </div>
              ))}
            </div>

            {/* Money tiles */}
            {(stats.total_pending_amount != null || stats.total_disbursed_amount != null) && (
              <div style={{ display: "flex", gap: 12, marginBottom: 24, flexWrap: "wrap" }}>
                {stats.total_pending_amount != null && (
                  <div style={{ background: "#fff3cd", borderRadius: 8, padding: "14px 20px", borderLeft: "4px solid #e0a800", minWidth: 220 }}>
                    <div style={{ fontSize: 18, fontWeight: 700, color: "#856404" }}>{formatMoney(stats.total_pending_amount)}</div>
                    <div style={{ fontSize: 12, color: "#856404", marginTop: 4 }}>Total Pending Amount</div>
                  </div>
                )}
                {stats.total_disbursed_amount != null && (
                  <div style={{ background: "#d4edda", borderRadius: 8, padding: "14px 20px", borderLeft: "4px solid #28a745", minWidth: 220 }}>
                    <div style={{ fontSize: 18, fontWeight: 700, color: "#155724" }}>{formatMoney(stats.total_disbursed_amount)}</div>
                    <div style={{ fontSize: 12, color: "#155724", marginTop: 4 }}>Total Disbursed Amount</div>
                  </div>
                )}
              </div>
            )}

            {/* Date range + filters */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 10, marginBottom: 16 }}>
              <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
                <div>
                  <label style={{ fontSize: 12, color: "#666", display: "block", marginBottom: 3 }}>From</label>
                  <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)}
                    style={{ padding: "6px 10px", borderRadius: 6, border: "1px solid #ccc", fontSize: 13 }} />
                </div>
                <div>
                  <label style={{ fontSize: 12, color: "#666", display: "block", marginBottom: 3 }}>To</label>
                  <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)}
                    style={{ padding: "6px 10px", borderRadius: 6, border: "1px solid #ccc", fontSize: 13 }} />
                </div>
                <div className="filter" onClick={handleApplyDates} style={{ cursor: "pointer", color: "#fff" }}>Apply</div>
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <select value={filterStatusId} onChange={(e) => setFilterStatusId(e.target.value)}
                  style={{ padding: "7px 12px", borderRadius: 6, border: "1px solid #ccc", fontSize: 13 }}>
                  <option value="">All Statuses</option>
                  {STATUSES.map((s) => (
                    <option key={s.id} value={s.id}>{s.label}</option>
                  ))}
                </select>
                <select value={filterLoanMode} onChange={(e) => setFilterLoanMode(e.target.value)}
                  style={{ padding: "7px 12px", borderRadius: 6, border: "1px solid #ccc", fontSize: 13 }}>
                  <option value="">All Types</option>
                  <option value="1">Micro</option>
                  <option value="2">Standard</option>
                </select>
                <div className="filter" onClick={handleFilter} style={{ cursor: "pointer", color: "#fff" }}>Filter</div>
                <div className="filter" onClick={handleClearFilters} style={{ cursor: "pointer", background: "#6c757d", color: "#fff" }}>Clear</div>
              </div>
            </div>

            {/* Table */}
            {listLoading ? (
              <Loader />
            ) : listError ? (
              <p style={{ color: "#dc3545" }}>{listError}</p>
            ) : (
              <Paginate
                totPages={totalPages}
                currentPage={page}
                pageClicked={handlePageChange}
              >
                <MDBTable responsive className="table">
                  <MDBTableHead>
                    <tr>
                      <th>Reference</th>
                      <th>Applicant</th>
                      <th>Type</th>
                      <th>Amount</th>
                      <th>Tenor</th>
                      <th>Status</th>
                      <th>Date Applied</th>
                      <th></th>
                    </tr>
                  </MDBTableHead>
                  <MDBTableBody>
                    {apps.length === 0 ? (
                      <tr>
                        <td colSpan={8} style={{ textAlign: "center", padding: 32, color: "#aaa" }}>
                          No loan applications found
                        </td>
                      </tr>
                    ) : (
                      apps.map((a) => (
                        <tr key={a.id}>
                          <td style={{ fontFamily: "monospace", fontSize: 12 }}>{a.reference_number || "—"}</td>
                          <td>{a.applicant_name || "—"}</td>
                          <td>
                            <span style={{
                              background: a.loan_mode === "Micro" ? "#e3f2fd" : "#f3e5f5",
                              color: a.loan_mode === "Micro" ? "#1565c0" : "#6a1b9a",
                              padding: "2px 8px", borderRadius: 10, fontSize: 11, fontWeight: 600,
                            }}>
                              {a.loan_mode}
                            </span>
                          </td>
                          <td>{formatMoney(a.amount)}</td>
                          <td>{a.tenor ? `${a.tenor}mo` : "—"}</td>
                          <td><StatusBadge status={a.status} /></td>
                          <td>{formatDate(a.submitted_at)}</td>
                          <td>
                            <span onClick={() => handleView(a)}
                              style={{ cursor: "pointer", color: "#007bff", fontWeight: 600, fontSize: 13 }}>
                              View
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </MDBTableBody>
                </MDBTable>
              </Paginate>
            )}
          </div>
        </div>
      </section>

      {/* Detail Drawer */}
      {drawerOpen && (
        <div
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1000, display: "flex", justifyContent: "flex-end" }}
          onClick={(e) => e.target === e.currentTarget && closeDrawer()}
        >
          <div style={{ background: "#f4f5f7", width: "min(700px, 100vw)", height: "100vh", display: "flex", flexDirection: "column" }}>

            {/* Header */}
            <div style={{ background: "#fff", padding: "16px 20px", borderBottom: "1px solid #e8e8e8", display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexShrink: 0 }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 16, color: "#222" }}>
                  {app.reference_number || selectedLoan?.reference_number || "Application Detail"}
                </div>
                <div style={{ display: "flex", gap: 8, marginTop: 6, flexWrap: "wrap" }}>
                  {(app.loan_mode != null || selectedLoan?.loan_mode) && (
                    <span style={{
                      background: !loanModeIsStandard(app.loan_mode ?? selectedLoan?.loan_mode) ? "#e3f2fd" : "#f3e5f5",
                      color: !loanModeIsStandard(app.loan_mode ?? selectedLoan?.loan_mode) ? "#1565c0" : "#6a1b9a",
                      padding: "2px 8px", borderRadius: 10, fontSize: 11, fontWeight: 600,
                    }}>
                      {loanModeLabel(app.loan_mode ?? selectedLoan?.loan_mode)}
                    </span>
                  )}
                  <StatusBadge status={STATUS_BY_ID[app.status_id] || selectedLoan?.status} />
                </div>
              </div>
              <button onClick={closeDrawer} style={{ background: "none", border: "none", fontSize: 22, cursor: "pointer", color: "#777" }}>✕</button>
            </div>

            {/* Body */}
            <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px" }}>
              {detailLoading ? (
                <Loader />
              ) : detailError ? (
                <p style={{ color: "#dc3545", textAlign: "center" }}>{detailError}</p>
              ) : (
                <>
                  <SectionCard title="Borrower">
                    <InfoRow label="Full Name" value={`${app.first_name || ""} ${app.middle_name || ""} ${app.surname || ""}`.replace(/\s+/g, " ").trim()} />
                    <InfoRow label="Phone" value={app.phone} />
                    <InfoRow label="Email" value={app.email} />
                    <InfoRow label="Date of Birth" value={formatDate(app.date_of_birth)} />
                    <InfoRow label="Gender" value={app.gender} />
                    <InfoRow label="BVN" value={app.masked_bvn} />
                    <InfoRow label="NIN" value={app.masked_nin} />
                  </SectionCard>

                  {(app.street || app.city || app.state) && (
                    <SectionCard title="Address">
                      <InfoRow label="House No." value={app.house_number} />
                      <InfoRow label="Street" value={app.street} />
                      <InfoRow label="Estate / Landmark" value={app.estate || app.landmark} />
                      <InfoRow label="City / LGA" value={[app.city, app.lga].filter(Boolean).join(", ")} />
                      <InfoRow label="State" value={app.state} />
                    </SectionCard>
                  )}

                  {app.loan_mode === 2 && app.employer_name && (
                    <SectionCard title="Employment">
                      <InfoRow label="Employer" value={app.employer_name} />
                      <InfoRow label="Job Title" value={app.job_title} />
                      <InfoRow label="Years Employed" value={app.years_employed} />
                      <InfoRow label="Monthly Income" value={formatMoney(app.monthly_income)} />
                    </SectionCard>
                  )}

                  <SectionCard title="Loan Setup">
                    <InfoRow label="Requested Amount" value={formatMoney(app.amount)} />
                    <InfoRow label="Tenor" value={app.tenor_months ? `${app.tenor_months} month${app.tenor_months !== 1 ? "s" : ""}` : null} />
                    <InfoRow label="Purpose" value={app.purpose} />
                    <InfoRow label="Monthly Interest Rate" value={app.interest_rate_snapshot != null ? `${(app.interest_rate_snapshot * 100).toFixed(0)}%` : null} />
                    <InfoRow label="Monthly Repayment" value={formatMoney(app.monthly_repayment)} />
                    <InfoRow label="Total Interest" value={formatMoney(app.total_interest)} />
                    <InfoRow label="Total Repayable" value={formatMoney(app.total_repayable)} />
                    <InfoRow label="Processing Fee" value={formatMoney(app.processing_fee)} />
                    <InfoRow label="Net Disbursed" value={formatMoney(app.net_disbursed)} />
                    {app.loan_mode === 1 && (
                      <InfoRow label="System Approved Amount" value={app.system_approved_amount != null ? formatMoney(app.system_approved_amount) : "Not yet assessed"} />
                    )}
                  </SectionCard>

                  <SectionCard title="Credit & Bank">
                    <InfoRow label="Bureau Score" value={app.bureau_score != null ? String(app.bureau_score) : "Not checked"} />
                    <InfoRow label="Bureau Provider" value={app.bureau_provider} />
                    <InfoRow label="Bureau Reference" value={app.bureau_reference} />
                    <InfoRow label="Bank" value={app.bank_name} />
                    <InfoRow label="Account Number" value={app.account_number} />
                    <InfoRow label="Account Name" value={app.account_name} />
                    <InfoRow label="Eligibility" value={app.eligibility_status === 1 ? "Passed" : app.eligibility_status != null ? "Failed" : "Not checked"} />
                    {app.eligibility_failure_reason && <InfoRow label="Eligibility Failure" value={app.eligibility_failure_reason} />}
                    <InfoRow label="Liveness Check" value={app.liveness_status === 1 ? "Passed" : app.liveness_status != null ? "Failed" : "Not done"} />
                    {app.loan_mode === 2 && (
                      <InfoRow label="Liveness Check 2" value={app.liveness2_status === 1 ? "Passed" : app.liveness2_status != null ? "Failed" : "Not done"} />
                    )}
                    <InfoRow label="Terms Signed" value={app.signed_at ? `Yes (${formatDate(app.signed_at)})` : "No"} />
                  </SectionCard>

                  {app.loan_mode === 1 && contacts.length > 0 && (
                    <SectionCard title="Emergency Contacts">
                      {contacts.map((c, i) => (
                        <div key={i} style={{ paddingBottom: 8, marginBottom: 8, borderBottom: i < contacts.length - 1 ? "1px solid #f0f0f0" : "none" }}>
                          <InfoRow label="Name" value={c.full_name} />
                          <InfoRow label="Phone" value={c.phone_number} />
                          <InfoRow label="Relationship" value={c.relationship_other || c.relationship} />
                        </div>
                      ))}
                    </SectionCard>
                  )}

                  {app.loan_mode === 2 && (
                    <SectionCard title={`Guarantors${!guarantors_complete ? " ⚠ Incomplete" : ""}`}>
                      {!guarantors_complete && (
                        <p style={{ fontSize: 12, color: "#856404", background: "#fff3cd", padding: "8px 10px", borderRadius: 5, margin: "0 0 10px" }}>
                          All guarantors must complete their submission before this loan can be approved.
                        </p>
                      )}
                      {guarantors.length === 0 ? (
                        <p style={{ fontSize: 13, color: "#aaa", margin: 0 }}>No guarantors added yet</p>
                      ) : (
                        guarantors.map((g, i) => (
                          <div key={g.id} style={{ paddingBottom: 10, marginBottom: 10, borderBottom: i < guarantors.length - 1 ? "1px solid #f0f0f0" : "none" }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                              <span style={{ fontWeight: 600, fontSize: 13 }}>{g.full_name}</span>
                              <span style={{ background: GUARANTOR_STATUS_COLORS[g.status] || "#6c757d", color: g.status === "Pending" ? "#333" : "#fff", padding: "2px 8px", borderRadius: 10, fontSize: 11, fontWeight: 600 }}>
                                {g.status}
                              </span>
                            </div>
                            <div style={{ fontSize: 12, color: "#888" }}>{g.email} · {g.phone_number}</div>
                            {g.opened_at && <div style={{ fontSize: 11, color: "#aaa", marginTop: 2 }}>Opened: {formatDate(g.opened_at)}</div>}
                            {g.completed_at && <div style={{ fontSize: 11, color: "#aaa" }}>Completed: {formatDate(g.completed_at)}</div>}
                          </div>
                        ))
                      )}
                    </SectionCard>
                  )}

                  {app.loan_mode === 2 && app.collateral_type && (
                    <SectionCard title="Collateral">
                      <InfoRow label="Type" value={app.collateral_type} />
                      <InfoRow label="Value" value={formatMoney(app.collateral_value)} />
                      <InfoRow label="Condition" value={app.collateral_condition} />
                      <InfoRow label="Location" value={app.collateral_location} />
                      <InfoRow label="Description" value={app.collateral_description} />
                    </SectionCard>
                  )}

                  {documents.length > 0 && (
                    <SectionCard title={`Documents (${documents.length})`}>
                      {documents.map((doc) => (
                        <div key={doc.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: "1px solid #f0f0f0" }}>
                          <span style={{ fontSize: 13, color: "#555" }}>{(doc.document_type || "").replace(/_/g, " ")}</span>
                          <a href={doc.file_url} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: "#007bff", fontWeight: 600 }}>View</a>
                        </div>
                      ))}
                    </SectionCard>
                  )}

                  {app.offered_amount != null && (
                    <SectionCard title="Offer">
                      <InfoRow label="Offered Amount" value={formatMoney(app.offered_amount)} />
                      <InfoRow label="Tenor" value={app.offered_tenor_months ? `${app.offered_tenor_months} month${app.offered_tenor_months !== 1 ? "s" : ""}` : null} />
                      <InfoRow label="Monthly Repayment" value={formatMoney(app.offer_monthly_repayment)} />
                      <InfoRow label="Total Repayable" value={formatMoney(app.offer_total_repayable)} />
                      <InfoRow label="Expires" value={formatDate(app.offer_expires_at)} />
                    </SectionCard>
                  )}

                  {app.disbursed_at && (
                    <SectionCard title="Disbursement">
                      <InfoRow label="Net Disbursed" value={formatMoney(app.net_disbursed)} />
                      <InfoRow label="Disbursed At" value={formatDate(app.disbursed_at)} />
                      <InfoRow label="First Repayment" value={formatDate(app.first_repayment_date)} />
                    </SectionCard>
                  )}

                  {schedule.length > 0 && (
                    <SectionCard title="Repayment Schedule">
                      <div style={{ overflowX: "auto" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                          <thead>
                            <tr style={{ background: "#f8f9fa" }}>
                              {["#", "Due Date", "Principal", "Interest", "Total", "Status"].map((h) => (
                                <th key={h} style={{ padding: "6px 8px", textAlign: "left", fontWeight: 600, color: "#555", borderBottom: "1px solid #e8e8e8", whiteSpace: "nowrap" }}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {schedule.map((row) => (
                              <tr key={row.installment_number}>
                                <td style={{ padding: "6px 8px" }}>{row.installment_number}</td>
                                <td style={{ padding: "6px 8px", whiteSpace: "nowrap" }}>{formatDate(row.due_date)}</td>
                                <td style={{ padding: "6px 8px" }}>{formatMoney(row.principal_amount)}</td>
                                <td style={{ padding: "6px 8px" }}>{formatMoney(row.interest_amount)}</td>
                                <td style={{ padding: "6px 8px" }}>{formatMoney(row.total_amount)}</td>
                                <td style={{ padding: "6px 8px" }}>
                                  <span style={{ background: "#fff3cd", color: "#856404", padding: "2px 6px", borderRadius: 8, fontSize: 11, fontWeight: 600 }}>
                                    Pending
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </SectionCard>
                  )}

                  {app.reject_reason && (
                    <SectionCard title="Rejection Reason">
                      <p style={{ fontSize: 13, color: "#555", margin: 0 }}>{app.reject_reason}</p>
                    </SectionCard>
                  )}

                  {/* Decision Actions */}
                  {hasActions && (
                    <SectionCard title="Actions">
                      {!actionPanel && (
                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                          {canApprove && (
                            <button
                              onClick={() => setActionPanel("approve")}
                              disabled={approveBlocked}
                              title={approveBlocked ? "All guarantors must complete their submission first" : ""}
                              style={{ background: approveBlocked ? "#aaa" : "#28a745", color: "#fff", border: "none", padding: "8px 16px", borderRadius: 6, cursor: approveBlocked ? "not-allowed" : "pointer", fontWeight: 600, fontSize: 13 }}
                            >
                              Approve
                            </button>
                          )}
                          {canRequestInfo && (
                            <button onClick={() => setActionPanel("request-info")}
                              style={{ background: "#fd7e14", color: "#fff", border: "none", padding: "8px 16px", borderRadius: 6, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>
                              Request Info
                            </button>
                          )}
                          {canReject && (
                            <button onClick={() => setActionPanel("reject")}
                              style={{ background: "#dc3545", color: "#fff", border: "none", padding: "8px 16px", borderRadius: 6, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>
                              Reject
                            </button>
                          )}
                        </div>
                      )}

                      {actionPanel === "approve" && (
                        <DecisionPanel title={app.loan_mode === 2 ? "Approve — Standard Loan" : "Approve — Micro Loan"}
                          onCancel={resetActionPanel} otpSent={otpSent} otpCode={otpCode} setOtpCode={setOtpCode}
                          otpLoading={otpLoading} submitLoading={submitLoading} otpError={otpError} submitError={submitError}
                          onSendOtp={handleSendOtp} onSubmit={handleSubmitDecision}>
                          {app.loan_mode === 2 ? (
                            <div style={{ display: "flex", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
                              <div>
                                <label style={{ fontSize: 12, color: "#666", display: "block", marginBottom: 3 }}>Offered Amount (₦)</label>
                                <input type="number" value={approvedAmount} onChange={(e) => setApprovedAmount(e.target.value)} placeholder="e.g. 20000000"
                                  style={{ padding: "7px 10px", borderRadius: 5, border: "1px solid #ccc", fontSize: 13, width: 190 }} />
                              </div>
                              <div>
                                <label style={{ fontSize: 12, color: "#666", display: "block", marginBottom: 3 }}>Tenor (months)</label>
                                <input type="number" value={approvedTenor} onChange={(e) => setApprovedTenor(e.target.value)} placeholder="1–6" min={1} max={6}
                                  style={{ padding: "7px 10px", borderRadius: 5, border: "1px solid #ccc", fontSize: 13, width: 100 }} />
                              </div>
                            </div>
                          ) : (
                            <p style={{ fontSize: 13, color: "#856404", background: "#fff3cd", padding: "8px 10px", borderRadius: 5, margin: "0 0 12px" }}>
                              <strong>This will disburse immediately.</strong> Approving a Micro loan credits the customer's balance on this click — no further action is needed from them.
                            </p>
                          )}
                        </DecisionPanel>
                      )}

                      {actionPanel === "request-info" && (
                        <DecisionPanel title="Request More Information" onCancel={resetActionPanel} otpSent={otpSent}
                          otpCode={otpCode} setOtpCode={setOtpCode} otpLoading={otpLoading} submitLoading={submitLoading}
                          otpError={otpError} submitError={submitError} onSendOtp={handleSendOtp} onSubmit={handleSubmitDecision}>
                          <textarea value={infoNote} onChange={(e) => setInfoNote(e.target.value)} rows={3}
                            placeholder="Describe what's needed from the customer (optional)..."
                            style={{ width: "100%", padding: "8px", borderRadius: 5, border: "1px solid #ccc", fontSize: 13, resize: "vertical", boxSizing: "border-box", marginBottom: 12 }} />
                        </DecisionPanel>
                      )}

                      {actionPanel === "reject" && (
                        <DecisionPanel title="Reject Application" onCancel={resetActionPanel} otpSent={otpSent}
                          otpCode={otpCode} setOtpCode={setOtpCode} otpLoading={otpLoading} submitLoading={submitLoading}
                          otpError={otpError} submitError={submitError} onSendOtp={handleSendOtp} onSubmit={handleSubmitDecision}>
                          <textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} rows={3}
                            placeholder="Rejection reason (required)..."
                            style={{ width: "100%", padding: "8px", borderRadius: 5, border: "1px solid #ccc", fontSize: 13, resize: "vertical", boxSizing: "border-box", marginBottom: 12 }} />
                        </DecisionPanel>
                      )}
                    </SectionCard>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

function DecisionPanel({ title, children, onCancel, otpSent, otpCode, setOtpCode, otpLoading, submitLoading, otpError, submitError, onSendOtp, onSubmit }) {
  return (
    <div style={{ background: "#f8f9fa", borderRadius: 6, padding: 14 }}>
      <p style={{ fontWeight: 600, fontSize: 13, color: "#333", margin: "0 0 12px" }}>{title}</p>
      {children}
      {!otpSent ? (
        <>
          <p style={{ fontSize: 12, color: "#666", margin: "0 0 10px" }}>
            An OTP will be sent to your registered email to confirm this action.
          </p>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={onSendOtp} disabled={otpLoading}
              style={{ background: otpLoading ? "#aaa" : "#007bff", color: "#fff", border: "none", padding: "7px 16px", borderRadius: 5, cursor: otpLoading ? "not-allowed" : "pointer", fontSize: 13, fontWeight: 600 }}>
              {otpLoading ? "Sending..." : "Send OTP"}
            </button>
            <button onClick={onCancel}
              style={{ background: "#6c757d", color: "#fff", border: "none", padding: "7px 14px", borderRadius: 5, cursor: "pointer", fontSize: 13 }}>
              Cancel
            </button>
          </div>
          {otpError && <p style={{ color: "red", fontSize: 12, marginTop: 8 }}>{otpError}</p>}
        </>
      ) : (
        <>
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 12, color: "#666", display: "block", marginBottom: 4 }}>Enter the 6-digit OTP sent to your email</label>
            <input type="text" value={otpCode} onChange={(e) => setOtpCode(e.target.value)} placeholder="000000" maxLength={6}
              style={{ padding: "8px 12px", borderRadius: 5, border: "1px solid #ccc", fontSize: 15, letterSpacing: 4, width: 140 }} />
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={onSubmit} disabled={submitLoading}
              style={{ background: submitLoading ? "#aaa" : "#28a745", color: "#fff", border: "none", padding: "7px 16px", borderRadius: 5, cursor: submitLoading ? "not-allowed" : "pointer", fontSize: 13, fontWeight: 600 }}>
              {submitLoading ? "Submitting..." : "Submit Decision"}
            </button>
            <button onClick={onCancel}
              style={{ background: "#6c757d", color: "#fff", border: "none", padding: "7px 14px", borderRadius: 5, cursor: "pointer", fontSize: 13 }}>
              Cancel
            </button>
          </div>
          {submitError && <p style={{ color: "red", fontSize: 12, marginTop: 8 }}>{submitError}</p>}
        </>
      )}
    </div>
  );
}

export default Loans;
